/**
 * XrRigSpace — the tracking origin as a comfort setting (XR P2 comfort slice, 2026-09-28).
 *
 * The seated floor offset, the height nudge and the recenter all move the TRACKING ORIGIN, never the head pose: the
 * base `local-floor` space three requested is kept, and an OFFSET reference space derived from it
 * (`getOffsetReferenceSpace(new XRRigidTransform(...))`) is handed to three with `renderer.xr.setReferenceSpace` —
 * WebXR's own rig transform. The camera, both controllers and the engine's XR input poll all read three's live space,
 * so they move together; the avatar rig (atlas.localMesh, the server-authoritative feet) is untouched.
 *
 * The math is pure (`originOffset` / `yawOfQuat` in lib/xr_locomotion.ts). THE RESET LAW: when the system recenters
 * (the user holds the Oculus button) the base space fires `reset` — offset spaces derived from it follow it, but an
 * app recenter stored before it now points at a stale origin, so it is dropped and the floor offset re-applied, with a
 * blink and the motion history cleared (a recenter is a discontinuity, never an angular velocity).
 *
 * THE STALE-SPACE LAW: three never clears its custom space on session end — a second session would read the dead
 * session's space (getViewerPose throws). `end()` clears it.
 */
import { originOffset, yawOfQuat, wrapPi, type Recenter } from '../../lib/xr_locomotion.js';

/** The slice of three's XRManager this needs. */
export interface XrManagerLike {
  getReferenceSpace(): XRReferenceSpace | null;
  setReferenceSpace(space: XRReferenceSpace | null): void;
  getFrame?(): XRFrame | null;
}

export interface RigSpaceState { active: boolean; floorOffsetM: number; recenter: Recenter | null; resets: number; recenters: number; applied: number; error: string | null }

export class XrRigSpace {
  private base: XRReferenceSpace | null = null;
  private rc: Recenter | null = null;
  private floor = 0;
  private key = '';
  private resets = 0;
  private recenters = 0;
  private applied = 0;
  private error: string | null = null;
  /** Called on a system reset (the owner blinks and clears its motion history). */
  onReset: (() => void) | null = null;
  private readonly resetHandler = (): void => {
    this.resets++;
    this.rc = null; // the system just recentered: an app recenter stored before it is stale
    this.key = '';
    this.apply(this.floor);
    this.onReset?.();
  };

  constructor(private readonly xrm: XrManagerLike) {}

  /** At sessionstart: take the base space three requested, listen for its reset, apply the floor offset. */
  start(floorOffsetM: number): void {
    this.end();
    try {
      this.xrm.setReferenceSpace(null); // read the BASE, never a custom space left from anything else
      this.base = this.xrm.getReferenceSpace();
      this.base?.addEventListener('reset', this.resetHandler);
      this.apply(floorOffsetM);
    } catch (err) {
      this.error = String((err as Error)?.message ?? err).slice(0, 120);
    }
  }

  /** At sessionend: forget the base and clear three's custom space (THE STALE-SPACE LAW). */
  end(): void {
    try { this.base?.removeEventListener('reset', this.resetHandler); } catch { /* the session is gone */ }
    this.base = null;
    this.rc = null;
    this.key = '';
    try { this.xrm.setReferenceSpace(null); } catch { /* not presenting */ }
  }

  /** (Re)apply the offset for this floor offset + the stored recenter. Cheap when nothing changed. */
  apply(floorOffsetM: number): void {
    this.floor = Number.isFinite(floorOffsetM) ? floorOffsetM : 0;
    if (!this.base) return;
    const rc = this.rc;
    const key = `${this.floor.toFixed(3)}|${rc ? `${rc.x.toFixed(3)},${rc.z.toFixed(3)},${rc.yaw.toFixed(4)}` : '-'}`;
    if (key === this.key) return;
    try {
      const o = originOffset(this.floor, rc);
      const Rigid = (globalThis as unknown as { XRRigidTransform?: new (p: DOMPointInit, q: DOMPointInit) => XRRigidTransform }).XRRigidTransform;
      if (!Rigid) throw new Error('XRRigidTransform unavailable');
      const space = (o.px === 0 && o.py === 0 && o.pz === 0 && o.qy === 0)
        ? this.base
        : this.base.getOffsetReferenceSpace(new Rigid({ x: o.px, y: o.py, z: o.pz }, { x: o.qx, y: o.qy, z: o.qz, w: o.qw }));
      this.xrm.setReferenceSpace(space === this.base ? null : space);
      this.key = key;
      this.applied++;
      this.error = null;
    } catch (err) {
      this.error = String((err as Error)?.message ?? err).slice(0, 120);
    }
  }

  /**
   * The app recenter: the head's current floor position and heading in the BASE space become the origin, so the head
   * reads at x = z = 0 facing −Z. Must run inside an XR frame (the viewer pose). Returns the head's heading in the
   * space that was live BEFORE (the yaw the rig must absorb to keep the world view still), or null when it could not.
   */
  recenter(): number | null {
    const frame = this.xrm.getFrame?.() ?? null;
    if (!this.base || !frame) return null;
    try {
      const pose = frame.getViewerPose(this.base);
      if (!pose) return null;
      const p = pose.transform.position, q = pose.transform.orientation;
      const yaw = yawOfQuat(q.x, q.y, q.z, q.w);
      const before = wrapPi(yaw - (this.rc?.yaw ?? 0));
      this.rc = { x: p.x, z: p.z, yaw };
      this.recenters++;
      this.apply(this.floor);
      return before;
    } catch (err) {
      this.error = String((err as Error)?.message ?? err).slice(0, 120);
      return null;
    }
  }

  get active(): boolean { return !!this.base; }

  state(): RigSpaceState {
    return { active: !!this.base, floorOffsetM: this.floor, recenter: this.rc ? { ...this.rc } : null, resets: this.resets, recenters: this.recenters, applied: this.applied, error: this.error };
  }
}
